import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch } from "react-redux";
import { getMyDynamicCriteria } from "../../../actions/api.js";
import { types as at, errorAction } from "@authzsvc/api-lib";
import {
  Button,
  Select,
  Form,
  Input,
  Modal,
  Typography,
  Spin,
  Table,
  Card,
  Space,
  Segmented,
  Tooltip,
} from "antd";
import {
  PlusOutlined,
  MinusOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
  InfoCircleOutlined,
  ApartmentOutlined,
} from "@ant-design/icons";
import {
  standardOperators,
  getOperatorsForDataType,
  excludedCriteriaIds,
} from "./utils";
import {
  emptyLeaf,
  emptySubgroup,
  emptyTree,
  backendToLocal,
  localToBackend,
  buildExpression,
} from "./criteriaLogicUtils";
import TooltipIcon from "../../common/TooltipIcon";
import { showErrorNotification } from "../../../util.js";

// Value inputs (used inside builder modal)

const ValueInput = ({ leaf, onChange, criteriaOptions, disabled }) => {
  const op = leaf.operator;
  if (
    !op ||
    op === "IsNull" ||
    op === "IsNotNull" ||
    op === "True" ||
    op === "False"
  ) {
    return null;
  }

  const match = criteriaOptions.find((c) => c.id === leaf.field);
  const placeholder = match?.exampleValues
    ? `Ex: ${match.exampleValues}`
    : "Value";

  if (op === "Between" || op === "NotBetween") {
    const vals = Array.isArray(leaf.values) ? leaf.values : ["", ""];
    return (
      <>
        <Input
          placeholder="Min"
          value={vals[0] || ""}
          onChange={(e) => onChange("values", [e.target.value, vals[1]])}
          style={{ width: 90 }}
          size="small"
          disabled={disabled}
        />
        <Input
          placeholder="Max"
          value={vals[1] || ""}
          onChange={(e) => onChange("values", [vals[0], e.target.value])}
          style={{ width: 90 }}
          size="small"
          disabled={disabled}
        />
      </>
    );
  }

  const displayVal = Array.isArray(leaf.values)
    ? leaf.values.join(", ")
    : leaf.values;

  return (
    <Input
      placeholder={placeholder}
      value={displayVal || ""}
      onChange={(e) => onChange("values", e.target.value)}
      style={{ width: 160 }}
      size="small"
      disabled={disabled}
    />
  );
};

const CriterionRow = ({
  leaf,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onRemove,
  canModify,
  fieldOptions,
  operatorOptions,
  criteriaOptions,
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        display: "flex",
        gap: 6,
        alignItems: "center",
        marginBottom: 6,
        flexWrap: "wrap",
      }}
    >
      <Select
        showSearch
        placeholder="Field"
        options={fieldOptions}
        value={leaf.field || undefined}
        onChange={onFieldChange}
        filterOption={(input, opt) =>
          (opt?.searchtext ?? opt?.label ?? "")
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        style={{ width: 240 }}
        size="small"
        disabled={!canModify}
      />
      <Select
        placeholder="Operator"
        options={operatorOptions}
        value={leaf.operator || undefined}
        onChange={onOperatorChange}
        style={{ width: 160 }}
        size="small"
        disabled={!canModify || !leaf.field}
      />
      <ValueInput
        leaf={leaf}
        onChange={(key, val) => onValueChange(key, val)}
        criteriaOptions={criteriaOptions}
        disabled={!canModify}
      />
      {canModify && (
        <Button
          size="small"
          danger={hovered}
          icon={<DeleteOutlined />}
          onClick={onRemove}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        />
      )}
    </div>
  );
};

const SubgroupBlock = ({
  item,
  groupIndex,
  canModify,
  andOrOptions,
  onOperatorChange,
  onRemove,
  onAddLeaf,
  children,
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      style={{
        border: "1px solid #91caff",
        borderRadius: 4,
        padding: 10,
        marginBottom: 8,
        background: "#e6f4ff",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Combine with
        </Typography.Text>
        <Segmented
          value={item.operator}
          onChange={onOperatorChange}
          options={andOrOptions}
          size="small"
          disabled={!canModify}
        />
      </div>
      {children}
      {canModify && (
        <Space style={{ marginTop: 4 }}>
          <Button size="small" icon={<PlusOutlined />} onClick={onAddLeaf}>
            Add criterion
          </Button>
          <Button
            size="small"
            danger={hovered}
            icon={<MinusOutlined />}
            onClick={onRemove}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
          >
            Remove sub-rule
          </Button>
        </Space>
      )}
    </div>
  );
};

// Tree builder rendered inside the Configure Logic modal

const TreeBuilder = ({
  tree,
  onChange,
  canModify,
  fieldOptions,
  criteriaOptions,
  getOperatorOptions,
  adjustOperatorForField,
  adjustValuesForOperator,
}) => {
  const mutate = (updater) => onChange(updater(tree));

  const updateLeaf = (groupIdx, leafIdx, key, value) =>
    mutate((t) => {
      const conditions = [...t.conditions];
      if (groupIdx === null) {
        const leaf = { ...conditions[leafIdx], [key]: value };
        if (key === "field") {
          leaf.operator = adjustOperatorForField(value, leaf.operator);
          if (leaf.operator !== conditions[leafIdx].operator) leaf.values = "";
        } else if (key === "operator") {
          leaf.values = adjustValuesForOperator(value, leaf.values);
        }
        conditions[leafIdx] = leaf;
      } else {
        const grp = conditions[groupIdx];
        const leaves = [...grp.conditions];
        const leaf = { ...leaves[leafIdx], [key]: value };
        if (key === "field") {
          leaf.operator = adjustOperatorForField(value, leaf.operator);
          if (leaf.operator !== leaves[leafIdx].operator) leaf.values = "";
        } else if (key === "operator") {
          leaf.values = adjustValuesForOperator(value, leaf.values);
        }
        leaves[leafIdx] = leaf;
        conditions[groupIdx] = { ...grp, conditions: leaves };
      }
      return { ...t, conditions };
    });

  const removeLeaf = (groupIdx, leafIdx) =>
    mutate((t) => {
      const conditions = [...t.conditions];
      if (groupIdx === null) {
        return { ...t, conditions: conditions.filter((_, i) => i !== leafIdx) };
      }
      const grp = conditions[groupIdx];
      const newLeaves = grp.conditions.filter((_, i) => i !== leafIdx);
      if (newLeaves.length === 0) {
        return {
          ...t,
          conditions: conditions.filter((_, i) => i !== groupIdx),
        };
      }
      conditions[groupIdx] = { ...grp, conditions: newLeaves };
      return { ...t, conditions };
    });

  const expression = buildExpression(tree, criteriaOptions);

  const hasRootLeaves = tree.conditions.some(
    (item) => item.conditions === undefined
  );

  return (
    <>
      <div
        style={{
          border: "1px solid #d9d9d9",
          borderRadius: 6,
          padding: 12,
          background: "#fafafa",
        }}
      >
        {/* Root operator */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 10,
          }}
        >
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            All conditions combined with
          </Typography.Text>
          <Segmented
            value={tree.operator}
            onChange={(op) => mutate((t) => ({ ...t, operator: op }))}
            options={andOrOptions}
            size="small"
            disabled={!canModify}
          />
        </div>

        {/* Column headers */}
        {hasRootLeaves && (
          <div
            style={{ display: "flex", gap: 6, marginBottom: 2, paddingLeft: 1 }}
          >
            <Typography.Text
              type="secondary"
              style={{ fontSize: 11, width: 240 }}
            >
              Field
            </Typography.Text>
            <Typography.Text
              type="secondary"
              style={{ fontSize: 11, width: 160 }}
            >
              Operator
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              Value
            </Typography.Text>
          </div>
        )}

        {/* Items */}
        {tree.conditions.map((item, i) => {
          if (item.conditions !== undefined) {
            return (
              <SubgroupBlock
                key={i}
                item={item}
                groupIndex={i}
                canModify={canModify}
                andOrOptions={andOrOptions}
                onOperatorChange={(op) =>
                  mutate((t) => {
                    const conditions = [...t.conditions];
                    conditions[i] = { ...conditions[i], operator: op };
                    return { ...t, conditions };
                  })
                }
                onRemove={() =>
                  mutate((t) => ({
                    ...t,
                    conditions: t.conditions.filter((_, idx) => idx !== i),
                  }))
                }
                onAddLeaf={() =>
                  mutate((t) => {
                    const conditions = [...t.conditions];
                    conditions[i] = {
                      ...conditions[i],
                      conditions: [...conditions[i].conditions, emptyLeaf()],
                    };
                    return { ...t, conditions };
                  })
                }
              >
                {item.conditions.map((leaf, j) => (
                  <CriterionRow
                    key={j}
                    leaf={leaf}
                    onFieldChange={(v) => updateLeaf(i, j, "field", v)}
                    onOperatorChange={(v) => updateLeaf(i, j, "operator", v)}
                    onValueChange={(key, v) => updateLeaf(i, j, key, v)}
                    onRemove={() => removeLeaf(i, j)}
                    canModify={canModify}
                    fieldOptions={fieldOptions}
                    operatorOptions={getOperatorOptions(leaf.field)}
                    criteriaOptions={criteriaOptions}
                  />
                ))}
              </SubgroupBlock>
            );
          }

          return (
            <CriterionRow
              key={i}
              leaf={item}
              onFieldChange={(v) => updateLeaf(null, i, "field", v)}
              onOperatorChange={(v) => updateLeaf(null, i, "operator", v)}
              onValueChange={(key, v) => updateLeaf(null, i, key, v)}
              onRemove={() => removeLeaf(null, i)}
              canModify={canModify}
              fieldOptions={fieldOptions}
              operatorOptions={getOperatorOptions(item.field)}
              criteriaOptions={criteriaOptions}
            />
          );
        })}

        {/* Action buttons */}
        {canModify && (
          <Space style={{ marginTop: 4 }}>
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() =>
                mutate((t) => ({
                  ...t,
                  conditions: [...t.conditions, emptyLeaf()],
                }))
              }
            >
              Add criterion
            </Button>
            <Tooltip title="Group multiple conditions together with their own AND / OR logic — e.g. (City is Geneva OR City is Meyrin)">
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() =>
                  mutate((t) => ({
                    ...t,
                    conditions: [...t.conditions, emptySubgroup()],
                  }))
                }
              >
                Add sub-rule
              </Button>
            </Tooltip>
          </Space>
        )}
      </div>

      {/* Readable expression */}
      {expression && (
        <div style={{ marginTop: 12 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Expression:{" "}
          </Typography.Text>
          <Typography.Text
            code
            style={{ fontSize: 12, wordBreak: "break-word" }}
          >
            {expression}
          </Typography.Text>
        </div>
      )}
    </>
  );
};

const andOrOptions = [
  { label: "AND", value: "And" },
  { label: "OR", value: "Or" },
];

const StandardGroupCriteria = ({
  canModifyGroup,
  standardCriteria,
  handleUpdate,
  loading,
  categoryOperator,
  onCategoryOperatorChange,
  builderModalOpen,
  onBuilderModalClose,
  onBuilderModalOpen,
}) => {
  const [tree, setTree] = useState(emptyTree);
  const treeRef = useRef(tree);
  const [criteriaOptions, setCriteriaOptions] = useState([]);
  const dispatch = useDispatch();

  // Builder modal state (open/close controlled by parent)
  const [modalTree, setModalTree] = useState(emptyTree);

  // Help modal state
  const [criteriaHelpOpen, setCriteriaHelpOpen] = useState(false);

  // Add modal state
  const [standardModalOpen, setStandardModalOpen] = useState(false);
  const [newStandardCriteria, setNewStandardCriteria] = useState({
    field: "",
    operator: "",
    values: "",
  });
  const [operatorOptions, setOperatorOptions] = useState(standardOperators);

  // Edit modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editCriteria, setEditCriteria] = useState(null);

  // Delete modal state
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [standardSelectedRows, setStandardSelectedRows] = useState([]);

  // Validation errors (shared between add and edit modals)
  const [validationError, setValidationError] = useState("");
  const [firstValueError, setFirstValueError] = useState("");
  const [secondValueError, setSecondValueError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [operatorError, setOperatorError] = useState("");

  useEffect(() => {
    const fetchCriteria = async () => {
      const result = await dispatch(getMyDynamicCriteria());
      if (result.type === errorAction(at.GET_MY_DYNAMIC_GROUP_CRITERIA)) {
        showErrorNotification(result.payload.title, result.payload.message);
      } else {
        setCriteriaOptions(result.payload.data);
      }
    };
    fetchCriteria();
  }, [dispatch]);

  useEffect(() => {
    const next = backendToLocal(standardCriteria);
    setTree(next);
    treeRef.current = next;
  }, [standardCriteria]);

  useEffect(() => {
    if (builderModalOpen) {
      setModalTree(JSON.parse(JSON.stringify(treeRef.current)));
    }
  }, [builderModalOpen]);

  const fieldOptions = useMemo(() => {
    if (!criteriaOptions) return [];
    const excluded = new Set(excludedCriteriaIds);
    const normal = [];
    const restricted = [];

    criteriaOptions.forEach((c) => {
      if (excluded.has(c.id)) return;
      const opt = {
        label: `${c.description} (${c.id})`,
        value: c.id,
        disabled: c.restricted,
        searchtext: `${c.id} ${c.description}`,
      };
      if (c.restricted) restricted.push(opt);
      else normal.push(opt);
    });

    normal.sort((a, b) => a.label.localeCompare(b.label));
    restricted.sort((a, b) => a.label.localeCompare(b.label));

    if (restricted.length > 0) {
      normal.push({
        label: "────── RESTRICTED ──────",
        value: "divider",
        disabled: true,
      });
    }

    return [...normal, ...restricted];
  }, [criteriaOptions]);

  const getOperatorOptions = (fieldId) => {
    const dataType = criteriaOptions.find((c) => c.id === fieldId)?.datatype;
    return dataType ? getOperatorsForDataType(dataType) : standardOperators;
  };

  const adjustOperatorForField = (fieldId, currentOperator) => {
    if (!currentOperator) return currentOperator;
    const dataType = criteriaOptions.find((c) => c.id === fieldId)?.datatype;
    const validOps = getOperatorsForDataType(dataType).map((o) => o.value);
    return validOps.includes(currentOperator) ? currentOperator : "";
  };

  const adjustValuesForOperator = (newOp, currentValues) => {
    if (["IsNull", "IsNotNull", "True", "False"].includes(newOp)) return "";
    if (
      (newOp === "Between" || newOp === "NotBetween") &&
      !Array.isArray(currentValues)
    )
      return ["", ""];
    if (
      newOp !== "Between" &&
      newOp !== "NotBetween" &&
      Array.isArray(currentValues)
    )
      return "";
    return currentValues;
  };

  // Flat leaves derived from tree (for table display)

  const flatLeaves = useMemo(() => {
    const leaves = [];
    tree.conditions.forEach((item, i) => {
      if (item.conditions !== undefined) {
        item.conditions.forEach((leaf, j) => {
          leaves.push({ ...leaf, _groupIdx: i, _leafIdx: j });
        });
      } else {
        leaves.push({ ...item, _groupIdx: null, _leafIdx: i });
      }
    });
    return leaves;
  }, [tree]);

  const standardDataSource = flatLeaves.map((leaf, k) => ({
    key: k,
    field: leaf.field,
    operator: leaf.operator,
    values: Array.isArray(leaf.values) ? leaf.values.join(", ") : leaf.values,
  }));

  const deleteConfirmationDataSource = standardSelectedRows.map((k) => ({
    key: k,
    field: flatLeaves[k]?.field,
    operator: flatLeaves[k]?.operator,
    values: Array.isArray(flatLeaves[k]?.values)
      ? flatLeaves[k].values.join(", ")
      : flatLeaves[k]?.values,
  }));

  const hasStandardSelections = standardSelectedRows.length > 0;

  // Immediate tree update + save

  const applyAndSave = (updater) => {
    const newTree = updater(tree);
    setTree(newTree);
    treeRef.current = newTree;
    handleUpdate(localToBackend(newTree));
  };

  const clearErrors = () => {
    setFieldError("");
    setOperatorError("");
    setValidationError("");
    setFirstValueError("");
    setSecondValueError("");
  };

  const validateCriteriaValue = (criteria) => {
    let fErr = "",
      opErr = "",
      firstErr = "",
      secondErr = "",
      err = "";

    if (!criteria.field) fErr = "Please select a field";
    if (!criteria.operator) opErr = "Please select an operator";

    if (
      criteria.operator === "IsNull" ||
      criteria.operator === "IsNotNull" ||
      criteria.operator === "True" ||
      criteria.operator === "False"
    ) {
      return {
        isValid: !fErr && !opErr,
        fieldError: fErr,
        operatorError: opErr,
        firstError: "",
        secondError: "",
        error: "",
      };
    }

    if (
      !criteria.values ||
      criteria.values === "" ||
      (Array.isArray(criteria.values) &&
        criteria.values.every((v) => !v || v.trim() === ""))
    ) {
      if (
        criteria.operator === "Between" ||
        criteria.operator === "NotBetween"
      ) {
        firstErr = "First value is required";
        secondErr = "Second value is required";
      } else {
        err = "Please enter a value";
      }
    } else if (criteria.field && criteria.operator) {
      const dataType = criteriaOptions
        .find((c) => c.id === criteria.field)
        ?.datatype?.toLowerCase();
      if (
        criteria.operator === "Between" ||
        criteria.operator === "NotBetween"
      ) {
        if (
          criteria.values[0]?.trim() &&
          dataType === "number" &&
          isNaN(criteria.values[0])
        )
          firstErr = "First value must be a valid number";
        if (
          criteria.values[1]?.trim() &&
          dataType === "number" &&
          isNaN(criteria.values[1])
        )
          secondErr = "Second value must be a valid number";
      } else {
        if (dataType === "number" && isNaN(criteria.values))
          err = "Please enter a valid number";
        if (
          dataType === "boolean" &&
          !["true", "false"].includes(criteria.values?.toLowerCase())
        )
          err = "Please enter true or false";
      }
    }

    return {
      isValid: !fErr && !opErr && !firstErr && !secondErr && !err,
      fieldError: fErr,
      operatorError: opErr,
      firstError: firstErr,
      secondError: secondErr,
      error: err,
    };
  };

  const handleStandardConditionChange = (key, value) => {
    setNewStandardCriteria((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === "field") {
        const newOp = adjustOperatorForField(value, updated.operator);
        updated.operator = newOp;
        if (newOp !== prev.operator) updated.values = "";
        const dataType = criteriaOptions.find((c) => c.id === value)?.datatype;
        setOperatorOptions(getOperatorsForDataType(dataType));
      } else if (key === "operator") {
        updated.values = adjustValuesForOperator(value, updated.values);
      }
      return updated;
    });
    if (key === "field") setFieldError("");
    else if (key === "operator") setOperatorError("");
    else {
      setValidationError("");
      setFirstValueError("");
      setSecondValueError("");
    }
  };

  const handleAddStandardCondition = () => {
    if (!canModifyGroup) return;
    const validation = validateCriteriaValue(newStandardCriteria);
    if (!validation.isValid) {
      setFieldError(validation.fieldError || "");
      setOperatorError(validation.operatorError || "");
      setFirstValueError(validation.firstError || "");
      setSecondValueError(validation.secondError || "");
      setValidationError(validation.error || "");
      return;
    }
    clearErrors();
    const newLeaf = {
      field: newStandardCriteria.field,
      operator: newStandardCriteria.operator,
      values: newStandardCriteria.values,
    };
    applyAndSave((t) => ({ ...t, conditions: [...t.conditions, newLeaf] }));
    setNewStandardCriteria({ field: "", operator: "", values: "" });
    setOperatorOptions(standardOperators);
    setStandardModalOpen(false);
  };

  const handleEditRow = (rowKey) => {
    const leaf = flatLeaves[rowKey];
    if (!leaf) return;
    clearErrors();
    const dataType = criteriaOptions.find((c) => c.id === leaf.field)?.datatype;
    setOperatorOptions(getOperatorsForDataType(dataType));
    setEditCriteria({ ...leaf });
    setEditModalOpen(true);
  };

  const handleEditChange = (key, value) => {
    if (key === "field") setFieldError("");
    else if (key === "operator") setOperatorError("");
    else {
      setValidationError("");
      setFirstValueError("");
      setSecondValueError("");
    }

    setEditCriteria((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === "field") {
        const newOp = adjustOperatorForField(value, updated.operator);
        updated.operator = newOp;
        if (newOp !== prev.operator) updated.values = "";
        const dataType = criteriaOptions.find((c) => c.id === value)?.datatype;
        setOperatorOptions(getOperatorsForDataType(dataType));
      } else if (key === "operator") {
        updated.values = adjustValuesForOperator(value, updated.values);
      }
      return updated;
    });
  };

  const handleSaveEdit = () => {
    if (!canModifyGroup) return;
    const validation = validateCriteriaValue(editCriteria);
    if (!validation.isValid) {
      setFieldError(validation.fieldError || "");
      setOperatorError(validation.operatorError || "");
      setFirstValueError(validation.firstError || "");
      setSecondValueError(validation.secondError || "");
      setValidationError(validation.error || "");
      return;
    }
    clearErrors();

    const { _groupIdx, _leafIdx } = editCriteria;
    const updatedLeaf = {
      field: editCriteria.field,
      operator: editCriteria.operator,
      values: editCriteria.values,
    };

    applyAndSave((t) => {
      const conditions = [...t.conditions];
      if (_groupIdx === null) {
        conditions[_leafIdx] = updatedLeaf;
      } else {
        const grp = conditions[_groupIdx];
        const leaves = [...grp.conditions];
        leaves[_leafIdx] = updatedLeaf;
        conditions[_groupIdx] = { ...grp, conditions: leaves };
      }
      return { ...t, conditions };
    });

    setEditModalOpen(false);
  };

  const handleRemoveSelectedCriteria = () => {
    if (!canModifyGroup || !hasStandardSelections) return;

    // Collect positions of leaves to remove (sorted descending to avoid index shift issues)
    const toRemove = standardSelectedRows
      .map((k) => flatLeaves[k])
      .filter(Boolean);

    applyAndSave((t) => {
      let conditions = [...t.conditions];

      // Remove root-level leaves first
      const rootIdxsToRemove = new Set(
        toRemove.filter((l) => l._groupIdx === null).map((l) => l._leafIdx)
      );
      conditions = conditions.filter((_, i) => !rootIdxsToRemove.has(i));

      // Remove subgroup leaves
      conditions = conditions
        .map((item) => {
          if (item.conditions === undefined) return item;
          // Find leaves in this group that should be removed
          const groupIdx = t.conditions.indexOf(item);
          const subIdxsToRemove = new Set(
            toRemove
              .filter((l) => l._groupIdx === groupIdx)
              .map((l) => l._leafIdx)
          );
          if (subIdxsToRemove.size === 0) return item;
          const newLeaves = item.conditions.filter(
            (_, j) => !subIdxsToRemove.has(j)
          );
          return newLeaves.length > 0
            ? { ...item, conditions: newLeaves }
            : null;
        })
        .filter(Boolean);

      return { ...t, conditions };
    });

    setStandardSelectedRows([]);
    setOpenDeleteModal(false);
  };

  const handleBuilderSave = () => {
    handleUpdate(localToBackend(modalTree));
    onBuilderModalClose();
  };

  const baseTableColumns = [
    { title: "Field", dataIndex: "field", key: "field" },
    { title: "Operator", dataIndex: "operator", key: "operator" },
    { title: "Values", dataIndex: "values", key: "values" },
  ];

  const standardTableColumns = [
    ...baseTableColumns,
    {
      title: "Actions",
      key: "actions",
      width: 80,
      render: (_, record) => (
        <Button
          icon={<EditOutlined />}
          color="primary"
          variant="filled"
          size="small"
          onClick={() => handleEditRow(record.key)}
          disabled={!canModifyGroup}
        />
      ),
    },
  ];

  const standardRowSelection = {
    selectedRowKeys: standardSelectedRows,
    onChange: (selectedRowKeys) => setStandardSelectedRows(selectedRowKeys),
    getCheckboxProps: () => ({ disabled: !canModifyGroup }),
  };

  // Value input rendering (for Add/Edit modals)
  const renderModalValueInput = (criteria, onChange) => {
    const op = criteria.operator;
    const inputStyle = { width: "100%", minWidth: 0 };

    const match = criteriaOptions.find((c) => c.id === criteria.field);
    const placeholder = match?.exampleValues
      ? `Ex: ${match.exampleValues}`
      : "Enter Value";

    switch (op) {
      case "Between":
      case "NotBetween":
        return (
          <>
            <Form.Item
              style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
              validateStatus={firstValueError ? "error" : ""}
              help={firstValueError}
            >
              <Input
                placeholder={placeholder}
                value={
                  Array.isArray(criteria.values) ? criteria.values[0] || "" : ""
                }
                onChange={(e) =>
                  onChange("values", [
                    e.target.value,
                    Array.isArray(criteria.values) ? criteria.values[1] : "",
                  ])
                }
                style={inputStyle}
                disabled={!op}
              />
            </Form.Item>
            <Form.Item
              style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
              validateStatus={secondValueError ? "error" : ""}
              help={secondValueError}
            >
              <Input
                placeholder={placeholder}
                value={
                  Array.isArray(criteria.values) ? criteria.values[1] || "" : ""
                }
                onChange={(e) =>
                  onChange("values", [
                    Array.isArray(criteria.values) ? criteria.values[0] : "",
                    e.target.value,
                  ])
                }
                style={inputStyle}
                disabled={!op}
              />
            </Form.Item>
          </>
        );
      case "IsNull":
      case "IsNotNull":
      case "True":
      case "False":
        return null;
      default:
        return (
          <Form.Item
            style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
            validateStatus={validationError ? "error" : ""}
            help={validationError}
          >
            <Input
              placeholder={placeholder}
              value={
                Array.isArray(criteria.values)
                  ? criteria.values.join(", ")
                  : criteria.values || ""
              }
              onChange={(e) => onChange("values", e.target.value)}
              style={inputStyle}
              disabled={!op}
            />
          </Form.Item>
        );
    }
  };

  return (
    <>
      {loading ? (
        <Card style={{ marginBottom: 16, textAlign: "center" }}>
          <Spin size="large" />
        </Card>
      ) : (
        <Card
          style={{ marginBottom: 16 }}
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                <FileTextOutlined
                  style={{ marginRight: 8, color: "#bfbfbf" }}
                />
                Personnel Information
              </Typography.Title>
              <Segmented
                size="small"
                value={categoryOperator}
                onChange={onCategoryOperatorChange}
                options={andOrOptions}
                disabled={!canModifyGroup || !standardCriteria}
              />
              <TooltipIcon
                size={16}
                title="Choose how criteria are combined: AND includes only people who match every condition, while OR includes people who match at least one condition. Changing this can significantly widen or narrow who is included."
              />
            </div>
          }
          extra={
            <Space>
              <Button
                color="green"
                variant="solid"
                icon={<PlusOutlined />}
                disabled={!canModifyGroup}
                onClick={() => {
                  clearErrors();
                  setNewStandardCriteria({
                    field: "",
                    operator: "",
                    values: "",
                  });
                  setOperatorOptions(standardOperators);
                  setStandardModalOpen(true);
                }}
              >
                Add Criteria
              </Button>
              {canModifyGroup && (
                <Button
                  icon={<ApartmentOutlined />}
                  onClick={onBuilderModalOpen}
                >
                  Advanced criteria logic
                </Button>
              )}
            </Space>
          }
        >
          <Table
            rowKey="key"
            style={{ overflow: "auto" }}
            pagination={false}
            columns={standardTableColumns}
            dataSource={standardDataSource}
            rowSelection={standardRowSelection}
            locale={{ emptyText: "No criteria defined" }}
          />
          {standardDataSource.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Button
                type="primary"
                danger
                icon={<DeleteOutlined />}
                disabled={!hasStandardSelections || !canModifyGroup}
                onClick={() => setOpenDeleteModal(true)}
              />
            </div>
          )}
        </Card>
      )}

      {/* Add Standard Criteria Modal */}
      <Modal
        open={standardModalOpen}
        onCancel={() => {
          setStandardModalOpen(false);
          clearErrors();
        }}
        width={900}
        centered
        title="Add Standard Group Criteria"
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setStandardModalOpen(false);
              clearErrors();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="add"
            color="green"
            variant="solid"
            onClick={handleAddStandardCondition}
          >
            Add
          </Button>,
        ]}
      >
        <Form layout="vertical">
          <div style={{ display: "flex", gap: 8, width: "100%" }}>
            <Form.Item
              style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
              validateStatus={fieldError ? "error" : ""}
              help={fieldError}
            >
              <Select
                showSearch
                placeholder="Select Field"
                options={fieldOptions}
                value={newStandardCriteria.field || undefined}
                onChange={(value) =>
                  handleStandardConditionChange("field", value)
                }
                filterOption={(input, opt) =>
                  (opt?.searchtext ?? opt?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item
              style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
              validateStatus={operatorError ? "error" : ""}
              help={operatorError}
            >
              <Select
                placeholder="Select Operator"
                options={operatorOptions}
                value={newStandardCriteria.operator || undefined}
                onChange={(value) =>
                  handleStandardConditionChange("operator", value)
                }
                disabled={!newStandardCriteria.field}
              />
            </Form.Item>
            {renderModalValueInput(newStandardCriteria, (key, val) =>
              handleStandardConditionChange(key, val)
            )}
          </div>
        </Form>
      </Modal>

      {/* Edit Standard Criteria Modal */}
      <Modal
        open={editModalOpen}
        onCancel={() => {
          setEditModalOpen(false);
          clearErrors();
        }}
        width={900}
        centered
        title="Edit Standard Group Criteria"
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setEditModalOpen(false);
              clearErrors();
            }}
          >
            Cancel
          </Button>,
          <Button
            key="confirm"
            color="green"
            variant="solid"
            onClick={handleSaveEdit}
          >
            Confirm
          </Button>,
        ]}
      >
        <Form layout="vertical">
          <div style={{ display: "flex", gap: 8, width: "100%" }}>
            <Form.Item
              style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
              validateStatus={fieldError ? "error" : ""}
              help={fieldError}
            >
              <Select
                showSearch
                placeholder="Select Field"
                options={fieldOptions}
                value={editCriteria?.field || undefined}
                onChange={(value) => handleEditChange("field", value)}
                filterOption={(input, opt) =>
                  (opt?.searchtext ?? opt?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item
              style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
              validateStatus={operatorError ? "error" : ""}
              help={operatorError}
            >
              <Select
                placeholder="Select Operator"
                options={operatorOptions}
                value={editCriteria?.operator || undefined}
                onChange={(value) => handleEditChange("operator", value)}
                disabled={!editCriteria?.field}
              />
            </Form.Item>
            {editCriteria &&
              renderModalValueInput(editCriteria, (key, val) =>
                handleEditChange(key, val)
              )}
          </div>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={openDeleteModal}
        onCancel={() => setOpenDeleteModal(false)}
        centered
        width={900}
        title={
          <span>
            <DeleteOutlined style={{ marginRight: 8, color: "#ff4d4f" }} />
            Confirm Deletion
          </span>
        }
        footer={[
          <Button key="cancel" onClick={() => setOpenDeleteModal(false)}>
            Cancel
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            onClick={handleRemoveSelectedCriteria}
          >
            Delete
          </Button>,
        ]}
      >
        <p>Are you sure you want to remove the selected standard criteria?</p>
        {standardSelectedRows.length > 0 && (
          <Table
            columns={baseTableColumns}
            dataSource={deleteConfirmationDataSource}
            size="small"
            pagination={false}
          />
        )}
      </Modal>

      {/* Criteria Logic Help Modal */}
      <Modal
        open={criteriaHelpOpen}
        onCancel={() => setCriteriaHelpOpen(false)}
        centered
        width={560}
        title={
          <span>
            <InfoCircleOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            How to use Criteria Logic
          </span>
        }
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => setCriteriaHelpOpen(false)}
          >
            Close
          </Button>,
        ]}
      >
        <Typography>
          <Typography.Paragraph>
            The <strong>Criteria Logic</strong> builder lets you define complex
            membership rules by combining multiple conditions using{" "}
            <strong>AND</strong> and <strong>OR</strong> logic.
          </Typography.Paragraph>
          <Typography.Title level={5}>Basic concepts</Typography.Title>
          <Typography.Paragraph>
            <ul>
              <li>
                <strong>Criterion</strong> — a single rule made up of a{" "}
                <em>Field</em> (e.g. department), an <em>Operator</em> (e.g.
                Equals), and a <em>Value</em> (e.g. IT).
              </li>
              <li>
                <strong>AND</strong> — all conditions must be true for a person
                to be included.
              </li>
              <li>
                <strong>OR</strong> — at least one condition must be true.
              </li>
            </ul>
          </Typography.Paragraph>
          <Typography.Title level={5}>Sub-rules</Typography.Title>
          <Typography.Paragraph>
            Use <strong>Add sub-rule</strong> to nest a set of criteria
            together. Each sub-rule has its own AND / OR combinator, allowing
            rules like:{" "}
            <em>"Department is IT AND (Building is 28 OR Building is 31)"</em>.
          </Typography.Paragraph>
          <Typography.Title level={5}>Expression preview</Typography.Title>
          <Typography.Paragraph>
            As you build your criteria, a plain-text summary appears at the
            bottom of the builder. Use it to double-check that the logic matches
            what you intended before saving.
          </Typography.Paragraph>
          <Typography.Paragraph type="secondary">
            Changes are only applied when you click <strong>Save</strong>.
          </Typography.Paragraph>
        </Typography>
      </Modal>

      {/* Configure Logic Modal (AND/OR builder) */}
      <Modal
        open={builderModalOpen}
        onCancel={onBuilderModalClose}
        width={800}
        centered
        title={
          <span>
            Configure Criteria Logic
            <InfoCircleOutlined
              style={{
                marginLeft: 8,
                color: "#1677ff",
                cursor: "pointer",
                fontSize: 16,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setCriteriaHelpOpen(true);
              }}
            />
          </span>
        }
        footer={[
          <Button key="cancel" onClick={onBuilderModalClose}>
            Cancel
          </Button>,
          <Button
            key="save"
            color="green"
            variant="solid"
            onClick={handleBuilderSave}
          >
            Save
          </Button>,
        ]}
      >
        <TreeBuilder
          tree={modalTree}
          onChange={setModalTree}
          canModify={canModifyGroup}
          fieldOptions={fieldOptions}
          criteriaOptions={criteriaOptions}
          getOperatorOptions={getOperatorOptions}
          adjustOperatorForField={adjustOperatorForField}
          adjustValuesForOperator={adjustValuesForOperator}
        />
      </Modal>
    </>
  );
};

export default StandardGroupCriteria;
